<template>
  <div class="technician-page">
    <header-portrate />
    <TabsComp :tabs="tabs" />

    <div class="technician-shell" :style="`direction:${lang.dir || 'ltr'}`">
      <div class="toolbar">
        <div>
          <h5 class="page-title">{{ t('technician_workbench', 'Technician Workbench') }}</h5>
          <div class="muted-line">{{ t('assigned_items', 'Assigned items') }}: {{ items.length }}</div>
        </div>
        <div class="toolbar-actions">
          <b-form-select v-model="filters.status" :options="statusOptions" class="filter-select" @change="loadItems" />
          <b-button size="sm" variant="primary" @click="loadItems" :disabled="loading">
            <v-icon small class="mr-1">mdi-refresh</v-icon>
            {{ t('refresh', 'Refresh') }}
          </b-button>
        </div>
      </div>

      <div class="summary-grid">
        <div class="summary-cell waiting">
          <span>{{ t('not_started', 'Not Started') }}</span>
          <strong>{{ summary.not_started }}</strong>
        </div>
        <div class="summary-cell">
          <span>{{ t('open', 'Open') }}</span>
          <strong>{{ summary.open }}</strong>
        </div>
        <div class="summary-cell paused">
          <span>{{ t('paused', 'Paused') }}</span>
          <strong>{{ summary.paused }}</strong>
        </div>
        <div class="summary-cell fixed">
          <span>{{ t('fixed', 'Fixed') }}</span>
          <strong>{{ summary.fixed }}</strong>
        </div>
      </div>

      <div class="workbench-grid">
        <aside class="items-pane">
          <div
            v-for="item in items"
            :key="item.row_id"
            :class="['item-row', { active: selected && selected.row_id === item.row_id }]"
            @click="selectItem(item)"
          >
            <div class="item-row-head">
              <strong>#{{ item.job_card_number || item.job_card_id }}</strong>
              <span :class="['status-pill', item.status_key]">{{ item.status_label }}</span>
            </div>
            <div class="item-name">{{ item.item_name || '-' }}</div>
            <div class="item-meta">
              <span>{{ item.customer_name || '-' }}</span>
              <span>{{ item.plate_number || item.device_summary || '-' }}</span>
            </div>
            <div class="item-meta small">
              <span>{{ t('received', 'Received') }}: {{ formatDate(item.received_date || item.row_created_date) }}</span>
              <span>{{ t('parts', 'Parts') }}: {{ (item.spare_summary && item.spare_summary.total) || 0 }}</span>
            </div>
            <div class="item-meta small">
              <span>{{ t('remaining_hours', 'Remaining') }}: {{ formatHours(item.timing && item.timing.remaining_hours) }}</span>
              <span>{{ t('deadline', 'Deadline') }}: {{ formatDate(item.timing && item.timing.deadline_at) }}</span>
            </div>
          </div>
          <div v-if="!items.length" class="empty-state">{{ t('no_data', 'No data') }}</div>
        </aside>

        <main class="details-pane" v-if="selected">
          <section class="detail-section">
            <div class="section-title">{{ t('job_card_information', 'Job Card Information') }}</div>
            <div class="info-grid">
              <div><span>{{ t('cardid', 'Job Card #') }}</span><strong>{{ selected.job_card_number || selected.job_card_id }}</strong></div>
              <div><span>{{ t('customer', 'Customer') }}</span><strong>{{ selected.customer_name || '-' }}</strong></div>
              <div><span>{{ t('mobile', 'Mobile') }}</span><strong>{{ selected.mobile || '-' }}</strong></div>
              <div><span>{{ t('delivery_date', 'Delivery Date') }}</span><strong>{{ formatDate(selected.delivery_date) }}</strong></div>
            </div>
          </section>

          <section class="detail-section">
            <div class="section-title">{{ t('device_information', 'Device Information') }}</div>
            <div v-if="hasDeviceInfo" class="info-grid">
              <div v-for="(value, key) in selected.device_info" :key="key">
                <span>{{ labelize(key) }}</span>
                <strong>{{ value || '-' }}</strong>
              </div>
            </div>
            <div v-else class="info-grid">
              <div><span>{{ t('plate_number', 'Plate Number') }}</span><strong>{{ selected.plate_number || '-' }}</strong></div>
              <div v-if="firstCar"><span>{{ t('model', 'Model') }}</span><strong>{{ firstCar.model || '-' }}</strong></div>
              <div v-if="firstCar"><span>{{ t('vin', 'VIN') }}</span><strong>{{ firstCar.vin || '-' }}</strong></div>
              <div v-if="firstCar"><span>{{ t('brand', 'Brand') }}</span><strong>{{ firstCar.brand || '-' }}</strong></div>
            </div>
          </section>

          <section class="detail-section">
            <div class="section-title">{{ t('service', 'Service') }}</div>
            <div class="item-detail-line">
              <strong>{{ selected.item_name || '-' }}</strong>
              <span :class="['status-pill', selected.status_key]">{{ selected.status_label }}</span>
            </div>
            <div class="last-note" v-if="selected.last_action">
              <span>{{ selected.last_cause_label || selected.last_action }}</span>
              <small>{{ selected.last_note || '-' }}</small>
            </div>
          </section>

          <section class="detail-section">
            <div class="section-title">{{ t('timing', 'Timing') }}</div>
            <div class="info-grid">
              <div><span>{{ t('expected_hours', 'Expected Hours') }}</span><strong>{{ formatHours(selected.timing && selected.timing.expected_hours) }}</strong></div>
              <div><span>{{ t('working_hours', 'Worked Hours') }}</span><strong>{{ formatHours(selected.timing && selected.timing.working_hours) }}</strong></div>
              <div><span>{{ t('remaining_hours', 'Remaining Hours') }}</span><strong>{{ formatHours(selected.timing && selected.timing.remaining_hours) }}</strong></div>
              <div><span>{{ t('planned_start', 'Planned Start') }}</span><strong>{{ formatDate(selected.timing && selected.timing.planned_start_at) }}</strong></div>
              <div><span>{{ t('deadline', 'Deadline') }}</span><strong>{{ formatDate(selected.timing && selected.timing.deadline_at) }}</strong></div>
              <div><span>{{ t('queue_before', 'Queue Before') }}</span><strong>{{ formatHours(selected.timing && selected.timing.queue_hours_before) }}</strong></div>
            </div>
          </section>

          <section class="detail-section actions-section">
            <div class="section-title">{{ t('action', 'Action') }}</div>
            <div class="pause-row">
              <b-form-select v-model="pauseForm.cause_key" :options="causeOptions" class="cause-select" />
              <b-form-textarea v-model="pauseForm.note" rows="2" max-rows="3" :placeholder="t('notes', 'Notes')" />
            </div>
            <div class="button-row">
              <b-button size="sm" variant="primary" :disabled="!canStart || actionLoading" @click="startItem">
                <v-icon small class="mr-1">mdi-play-circle-outline</v-icon>
                {{ t('start', 'Start') }}
              </b-button>
              <b-button size="sm" variant="success" :disabled="selected.status_key !== 'open' || actionLoading" @click="markFixed">
                <v-icon small class="mr-1">mdi-check-circle-outline</v-icon>
                {{ t('fixed', 'Fixed') }}
              </b-button>
              <b-button size="sm" variant="warning" :disabled="selected.status_key !== 'open' || actionLoading" @click="pauseItem">
                <v-icon small class="mr-1">mdi-pause-circle-outline</v-icon>
                {{ t('pause', 'Pause') }}
              </b-button>
              <b-button size="sm" variant="outline-secondary" :disabled="selected.status_key !== 'paused' || actionLoading" @click="resumeItem">
                <v-icon small class="mr-1">mdi-play-circle-outline</v-icon>
                {{ t('resume', 'Resume') }}
              </b-button>
            </div>
          </section>

          <section class="detail-section">
            <div class="section-title">{{ t('attachement', 'Attachments') }}</div>
            <div class="attachment-form">
              <input
                ref="issueFiles"
                type="file"
                multiple
                accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                @change="selectIssueFiles"
              />
              <b-button size="sm" variant="primary" :disabled="attachmentLoading || !issueFiles.length" @click="uploadIssueFiles">
                <v-icon small class="mr-1">mdi-upload</v-icon>
                {{ t('upload', 'Upload') }}
              </b-button>
            </div>
            <div v-if="issueFiles.length" class="selected-files">
              <span v-for="(file, index) in issueFiles" :key="`${file.name}-${index}`">{{ file.name }}</span>
            </div>
            <v-simple-table dense class="parts-table">
              <thead>
                <tr>
                  <th>{{ t('file_type', 'Type') }}</th>
                  <th>{{ t('file_name', 'File') }}</th>
                  <th>{{ t('file_size', 'Size') }}</th>
                  <th>{{ t('download', 'Download') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="file in issueAttachments" :key="file.id">
                  <td>{{ attachmentType(file) }}</td>
                  <td>{{ file.title || file.file_name }}</td>
                  <td>{{ formatFileSize(file.file_size) }}</td>
                  <td>
                    <b-button size="sm" variant="outline-primary" @click="openAttachment(file)">
                      <v-icon small>mdi-open-in-new</v-icon>
                    </b-button>
                  </td>
                </tr>
                <tr v-if="!issueAttachments.length">
                  <td colspan="4" class="text-center">{{ t('no_data', 'No data') }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>

          <section class="detail-section">
            <div class="section-title">{{ t('request_spare_parts', 'Request Spare Parts') }}</div>
            <div class="parts-form">
              <b-form-input v-model="partForm.item_name" :placeholder="t('item_name', 'Item name')" />
              <b-form-input v-model.number="partForm.quantity" type="number" min="1" step="1" :placeholder="t('quantity', 'Quantity')" />
              <b-form-input v-model="partForm.note" :placeholder="t('notes', 'Notes')" />
              <b-button size="sm" variant="primary" :disabled="partLoading" @click="createPartRequest">
                <v-icon small class="mr-1">mdi-plus</v-icon>
                {{ t('add', 'Add') }}
              </b-button>
            </div>

            <v-simple-table dense class="parts-table">
              <thead>
                <tr>
                  <th>{{ t('item_name', 'Item') }}</th>
                  <th>{{ t('quantity', 'Qty') }}</th>
                  <th>{{ t('status', 'Status') }}</th>
                  <th>{{ t('action', 'Action') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="request in partRequests" :key="request.id">
                  <td>{{ request.item_name }}</td>
                  <td>{{ request.quantity }}</td>
                  <td>{{ request.status }}</td>
                  <td>
                    <b-button
                      v-if="canConfirmPart(request)"
                      size="sm"
                      variant="success"
                      class="mr-1"
                      @click="confirmPartRequest(request, 'fulfilled')"
                    >
                      <v-icon small>mdi-check</v-icon>
                    </b-button>
                    <b-button
                      v-if="canConfirmPart(request)"
                      size="sm"
                      variant="outline-danger"
                      @click="confirmPartRequest(request, 'rejected')"
                    >
                      <v-icon small>mdi-close</v-icon>
                    </b-button>
                  </td>
                </tr>
                <tr v-if="!partRequests.length">
                  <td colspan="4" class="text-center">{{ t('no_data', 'No data') }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </section>
        </main>

        <main class="details-pane empty-detail" v-else>
          {{ t('no_data', 'No data') }}
        </main>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import HeaderPortrate from '@/components/Header-Portrate.vue'
import Footer from '@/components/Footer.vue'
import TabsComp from '@/components/tabsComp.vue'
import { getVisibleTabsForRoute } from '@/menu/menuService'

const DEFAULT_PAUSE_CAUSES = [
  { cause_key: 'waiting_spare_part', name_ar: 'انتظار قطعة غيار', name_en: 'Waiting for spare part' },
  { cause_key: 'waiting_customer_approval', name_ar: 'انتظار موافقة العميل', name_en: 'Waiting for customer approval' },
  { cause_key: 'needs_supervisor_review', name_ar: 'يحتاج مراجعة المشرف', name_en: 'Needs supervisor review' },
  { cause_key: 'tools_unavailable', name_ar: 'الأدوات غير متوفرة', name_en: 'Tools unavailable' },
  { cause_key: 'diagnosis_required', name_ar: 'يحتاج إلى تشخيص', name_en: 'Diagnosis required' },
  { cause_key: 'quality_recheck', name_ar: 'إعادة فحص الجودة', name_en: 'Quality recheck' },
  { cause_key: 'other', name_ar: 'أخرى', name_en: 'Other' },
]

export default {
  components: { HeaderPortrate, Footer, TabsComp },
  data() {
    return {
      loading: false,
      actionLoading: false,
      partLoading: false,
      attachmentLoading: false,
      items: [],
      selected: null,
      causes: [],
      partRequests: [],
      issueAttachments: [],
      issueFiles: [],
      filters: {
        status: 'active',
      },
      pauseForm: {
        cause_key: '',
        note: '',
      },
      partForm: {
        item_name: '',
        quantity: 1,
        note: '',
      },
    }
  },
  computed: {
    lang() {
      if (this.$cookies.get(this.$store.state.LangCooki) != null) {
        if (this.$cookies.get(this.$store.state.LangCooki) == 'en') return this.$store.state.lang.en
        if (this.$cookies.get(this.$store.state.LangCooki) == 'ur') return this.$store.state.lang.ur
        return this.$store.state.lang.ar
      }
      if (this.$store.state.deflang == 'en') return this.$store.state.lang.en
      if (this.$store.state.deflang == 'ur') return this.$store.state.lang.ur
      return this.$store.state.lang.ar
    },
    tabs() {
      return getVisibleTabsForRoute(this.$store.getters['state'], this.lang, this.$route.path)
    },
    statusOptions() {
      return [
        { text: this.t('active', 'Active'), value: 'active' },
        { text: this.t('not_started', 'Not Started'), value: 'not_started' },
        { text: this.t('open', 'Open'), value: 'open' },
        { text: this.t('paused', 'Paused'), value: 'paused' },
        { text: this.t('fixed', 'Fixed'), value: 'fixed' },
        { text: this.t('all', 'All'), value: 'all' },
      ]
    },
    causeOptions() {
      const empty = [{ text: this.t('chose_from_list', 'Choose from list'), value: '' }]
      return empty.concat(this.causes.map((cause) => ({
        text: this.lang.langname == 'ar' ? (cause.name_ar || cause.name_en) : (cause.name_en || cause.name_ar),
        value: cause.cause_key,
      })))
    },
    summary() {
      return this.items.reduce((acc, item) => {
        const key = item.status_key || 'open'
        acc[key] = (acc[key] || 0) + 1
        return acc
      }, { not_started: 0, open: 0, paused: 0, fixed: 0 })
    },
    hasDeviceInfo() {
      return this.selected && this.selected.device_info && Object.keys(this.selected.device_info).length > 0
    },
    firstCar() {
      return this.selected && this.selected.cars_info && this.selected.cars_info.length ? this.selected.cars_info[0] : null
    },
    canStart() {
      if (!this.selected) return false
      if (this.selected.status_key === 'not_started') return true

      const assignment = this.selected.assignment || {}
      return Number(assignment.status) === 1 && !assignment.started_date && !assignment.last_started_at
    },
  },
  created() {
    this.loadCauses()
    this.loadItems()
  },
  methods: {
    t(key, fallback) {
      return (this.lang && this.lang[key]) || fallback
    },
    async postApi(type, payload = {}) {
      const post = new FormData()
      post.append('defBranch', localStorage.getItem('defBranch') || '1')
      post.append('financeYear', localStorage.getItem('financeYear') || '')
      post.append('type', type)
      post.append('auth', this.$cookies.get(this.$store.state.COOKIEPhase))
      // The legacy API rejects requests without a data[] field as error 213,
      // even when the authentication cookie is valid. Some read endpoints,
      // such as getTechnicianPauseCauses, do not otherwise need a payload.
      post.append('data[all]', '1')
      Object.keys(payload).forEach((key) => {
        post.append(`data[${key}]`, payload[key])
      })
      const response = await fetch(this.$store.state.SAMCOTEC.r_path, { method: 'POST', body: post })
      return response.json()
    },
    async loadCauses() {
      try {
        const res = await this.postApi('getTechnicianPauseCauses')
        const causes = res && res.error && res.error.number === 200 && res.results
          ? res.results.data
          : []
        this.causes = Array.isArray(causes) && causes.length ? causes : DEFAULT_PAUSE_CAUSES
      } catch (error) {
        console.error('Technician pause causes fetch failed.', error)
        this.causes = DEFAULT_PAUSE_CAUSES
      }
    },
    async loadItems() {
      this.loading = true
      try {
        const res = await this.postApi('getTechnicianJobItems', { status: this.filters.status })
        if (res && res.error && res.error.number === 200) {
          this.items = res.results.data || []
          const currentId = this.selected ? this.selected.row_id : 0
          this.selected = this.items.find((row) => row.row_id === currentId) || this.items[0] || null
          if (this.selected) {
            await this.loadPartRequests()
            await this.loadIssueAttachments()
          }
        } else if (res && res.error) {
          window.alert(res.error.message || 'Error')
        }
      } finally {
        this.loading = false
      }
    },
    async selectItem(item) {
      this.selected = item
      this.pauseForm = { cause_key: '', note: '' }
      this.partForm = { item_name: '', quantity: 1, note: '' }
      this.issueFiles = []
      await this.loadPartRequests()
      await this.loadIssueAttachments()
    },
    async updateItem(action, payload = {}) {
      if (!this.selected) return
      this.actionLoading = true
      try {
        const res = await this.postApi('updateTechnicianJobItemStatus', {
          row_id: this.selected.row_id,
          action,
          ...payload,
        })
        if (res && res.error && res.error.number === 200) {
          this.pauseForm = { cause_key: '', note: '' }
          await this.loadItems()
        } else if (res && res.error) {
          window.alert(res.error.message || 'Error')
        } else {
          window.alert('Unable to update item status')
        }
      } catch (error) {
        console.error('Technician item status update failed.', error)
        window.alert((error && error.message) || 'Unable to update item status')
      } finally {
        this.actionLoading = false
      }
    },
    startItem() {
      this.updateItem('started', { note: this.pauseForm.note || '' })
    },
    markFixed() {
      this.updateItem('fixed', { note: this.pauseForm.note || '' })
    },
    pauseItem() {
      if (!this.pauseForm.cause_key) {
        window.alert(this.t('chose_from_list', 'Choose from list'))
        return
      }
      this.updateItem('paused', {
        cause_key: this.pauseForm.cause_key,
        note: this.pauseForm.note || '',
      })
    },
    resumeItem() {
      this.updateItem('resumed', { note: this.pauseForm.note || '' })
    },
    async loadPartRequests() {
      if (!this.selected) return
      const res = await this.postApi('getTechnicianPartRequests', { job_card_id: this.selected.job_card_id })
      if (res && res.error && res.error.number === 200) {
        this.partRequests = res.results.data || []
      }
    },
    async loadIssueAttachments() {
      if (!this.selected) {
        this.issueAttachments = []
        return
      }
      const res = await this.postApi('getTechnicianJobItemFiles', { row_id: this.selected.row_id })
      if (res && res.error && res.error.number === 200) {
        this.issueAttachments = res.results.data || []
      }
    },
    selectIssueFiles(event) {
      const files = event && event.target && event.target.files ? Array.from(event.target.files) : []
      this.issueFiles = this.issueFiles.concat(files)
    },
    async uploadIssueFiles() {
      if (!this.selected || !this.issueFiles.length) return
      this.attachmentLoading = true
      try {
        const post = new FormData()
        post.append('defBranch', localStorage.getItem('defBranch') || '1')
        post.append('financeYear', localStorage.getItem('financeYear') || '')
        post.append('type', 'uploadTechnicianJobItemFiles')
        post.append('auth', this.$cookies.get(this.$store.state.COOKIEPhase))
        post.append('data[row_id]', this.selected.row_id)
        this.issueFiles.forEach((file, index) => {
          post.append(`data[files][${index}]`, file)
        })
        const response = await fetch(this.$store.state.SAMCOTEC.r_path, { method: 'POST', body: post })
        const res = await response.json()
        if (res && res.error && res.error.number === 200) {
          this.issueFiles = []
          if (this.$refs.issueFiles) this.$refs.issueFiles.value = ''
          await this.loadIssueAttachments()
        } else if (res && res.error) {
          window.alert(res.error.message || 'Error')
        }
      } finally {
        this.attachmentLoading = false
      }
    },
    async createPartRequest() {
      if (!this.selected || !this.partForm.item_name || Number(this.partForm.quantity) <= 0) return
      this.partLoading = true
      try {
        const res = await this.postApi('createTechnicianPartRequest', {
          row_id: this.selected.row_id,
          item_name: this.partForm.item_name,
          quantity: this.partForm.quantity,
          note: this.partForm.note || '',
        })
        if (res && res.error && res.error.number === 200) {
          this.partForm = { item_name: '', quantity: 1, note: '' }
          await this.loadPartRequests()
          await this.loadItems()
        } else if (res && res.error) {
          window.alert(res.error.message || 'Error')
        }
      } finally {
        this.partLoading = false
      }
    },
    canConfirmPart(request) {
      return request && ['approved', 'ordered'].includes(request.status)
    },
    async confirmPartRequest(request, status) {
      const res = await this.postApi('confirmTechnicianPartRequest', {
        request_id: request.id,
        status,
        note: status === 'fulfilled' ? 'Received by technician' : 'Not received by technician',
      })
      if (res && res.error && res.error.number === 200) {
        await this.loadPartRequests()
        await this.loadItems()
      } else if (res && res.error) {
        window.alert(res.error.message || 'Error')
      }
    },
    formatDate(value) {
      if (!value) return '-'
      return String(value).slice(0, 16)
    },
    formatHours(value) {
      const hours = Number(value || 0)
      if (!Number.isFinite(hours)) return '0 h'
      return `${Math.round(hours * 100) / 100} h`
    },
    formatFileSize(value) {
      const bytes = Number(value || 0)
      if (!Number.isFinite(bytes) || bytes <= 0) return '-'
      if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
      return `${Math.round((bytes / 1024 / 1024) * 100) / 100} MB`
    },
    attachmentType(file) {
      const type = String((file && file.file_type) || '')
      if (type.indexOf('image') === 0) return this.t('image', 'Image')
      if (type.indexOf('video') === 0) return this.t('video', 'Video')
      return this.t('attachment', 'Attachment')
    },
    openAttachment(file) {
      const src = file && (file.src || file.file_name)
      if (src) window.open(src, '_blank')
    },
    labelize(key) {
      return String(key || '').replace(/_/g, ' ')
    },
  },
}
</script>

<style scoped>
.technician-shell {
  padding: 14px 18px 24px;
  background: #f5f6f8;
  min-height: calc(100vh - 140px);
}
.toolbar,
.summary-grid,
.workbench-grid {
  max-width: 1440px;
  margin: 0 auto;
}
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.page-title {
  margin: 0;
  font-weight: 700;
}
.muted-line {
  color: #667085;
  font-size: 12px;
}
.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.filter-select {
  width: 160px;
}
.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}
.summary-cell {
  border: 1px solid #d8dde6;
  background: #fff;
  border-radius: 6px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.summary-cell span {
  color: #667085;
  font-size: 12px;
}
.summary-cell strong {
  font-size: 20px;
}
.summary-cell.waiting strong {
  color: #344054;
}
.summary-cell.paused strong {
  color: #9a5b00;
}
.summary-cell.fixed strong {
  color: #087443;
}
.workbench-grid {
  display: grid;
  grid-template-columns: minmax(300px, 34%) minmax(0, 1fr);
  gap: 12px;
}
.items-pane,
.details-pane {
  background: #fff;
  border: 1px solid #d8dde6;
  border-radius: 6px;
}
.items-pane {
  max-height: calc(100vh - 250px);
  overflow: auto;
}
.item-row {
  border-bottom: 1px solid #eceff3;
  padding: 11px 12px;
  cursor: pointer;
}
.item-row:hover,
.item-row.active {
  background: #eef4ff;
}
.item-row-head,
.item-meta,
.item-detail-line,
.button-row,
.parts-form {
  display: flex;
  align-items: center;
  gap: 8px;
}
.item-row-head,
.item-meta,
.item-detail-line {
  justify-content: space-between;
}
.item-name {
  margin: 5px 0;
  font-weight: 600;
}
.item-meta {
  color: #667085;
  font-size: 12px;
}
.item-meta.small {
  margin-top: 4px;
  font-size: 11px;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  min-width: 58px;
  justify-content: center;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  background: #e8edf3;
  color: #293241;
}
.status-pill.not_started {
  background: #eef2f6;
  color: #344054;
}
.status-pill.paused {
  background: #fff3cd;
  color: #7a4b00;
}
.status-pill.fixed {
  background: #dff6e9;
  color: #075b34;
}
.details-pane {
  padding: 12px;
  min-height: 520px;
}
.detail-section {
  border-bottom: 1px solid #eceff3;
  padding: 0 0 12px;
  margin-bottom: 12px;
}
.detail-section:last-child {
  border-bottom: 0;
  margin-bottom: 0;
}
.section-title {
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #2f3a4a;
}
.info-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}
.info-grid div {
  border: 1px solid #edf0f5;
  border-radius: 5px;
  padding: 8px;
  min-width: 0;
}
.info-grid span {
  display: block;
  color: #667085;
  font-size: 11px;
  margin-bottom: 3px;
}
.info-grid strong {
  display: block;
  font-size: 13px;
  word-break: break-word;
}
.last-note {
  margin-top: 8px;
  padding: 8px;
  border-radius: 5px;
  background: #f8fafc;
  color: #475467;
}
.last-note span,
.last-note small {
  display: block;
}
.pause-row {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 8px;
  margin-bottom: 8px;
}
.button-row {
  flex-wrap: wrap;
}
.parts-form {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) 100px minmax(180px, 1fr) auto;
  margin-bottom: 10px;
}
.attachment-form {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) auto;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
.attachment-form input {
  border: 1px solid #d8dde6;
  border-radius: 5px;
  padding: 6px;
  background: #fff;
}
.selected-files {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
.selected-files span {
  border: 1px solid #d8dde6;
  border-radius: 5px;
  padding: 3px 6px;
  background: #f8fafc;
  font-size: 12px;
}
.parts-table {
  border: 1px solid #edf0f5;
}
.empty-state,
.empty-detail {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667085;
  min-height: 180px;
}
@media (max-width: 960px) {
  .toolbar,
  .toolbar-actions,
  .workbench-grid,
  .pause-row,
  .parts-form,
  .attachment-form {
    grid-template-columns: 1fr;
    flex-direction: column;
    align-items: stretch;
  }
  .summary-grid,
  .info-grid {
    grid-template-columns: 1fr;
  }
  .filter-select {
    width: 100%;
  }
  .items-pane {
    max-height: none;
  }
}
</style>
